import React, { ReactNode, useRef, useState } from 'react';
import { Page, PageHeader } from 'components';
import { Command, CommandList } from 'enums';
import { TextField, Dropdown, Col, Button, ButtonGroup, Modal } from "@cfa/react-components";
import toast, { Toaster } from 'react-hot-toast';
import { CommandService, MruService, parseAxiosError } from 'services';
import { Overlay, Popover, Row } from 'react-bootstrap';
import { useHistory, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IMruCommand } from 'interfaces';
import './Commands';
import { DeviceSearchModal } from './DeviceSearchModal';

export function Commands() {

    // check for thingID passed in from devices page
    var defaultThingID = ""
    const searchParams = new URLSearchParams(useLocation().search);
    if (searchParams.has("thingID")) {
        defaultThingID = searchParams.get("thingID") || "";
    }

    const getTopicFromThingID = (thingID) => {
        var parts = thingID.split(".");

        if (parts.length !== 5 || parts[4].length !== 5) {
            return "";
        }

        return `bridge/${parts[4]}/thing/${thingID}/update/command`;
    }

    const [loading, setLoading] = useState(false);
    
    // mru button and popover
    const mruButtonRef = useRef(null);
    const [mruOpen, setMruOpen] = useState(false);

    // search button
    const [searchOpen, setSearchOpen] = useState(false);

    const [thingID, setThingID] = useState(defaultThingID);
    const [command, setCommand] = useState<Command>(Command.UpdatePerformanceDataInterval);
    const [commandValue, setCommandValue] = useState("");

    const currentTopic = getTopicFromThingID(thingID);
    const currentCommandPayload = JSON.stringify({event: "command", command: command, commandValue: commandValue}, null, "\t");
    const [currentResponsePayload, setCurrentResponsePayload] = useState("");

    const isValid = thingID !== "" && commandValue !== "" && currentTopic !== "";

    const reset = () => {
        setThingID("");
        setCommand(Command.UpdatePerformanceDataInterval);
        setCommandValue("");
        setCurrentResponsePayload("");
    }

    const onSubmit = () => {

        if (!isValid) return;
        
        setLoading(true);

        CommandService.commandDevice(thingID, command, commandValue)
            .then((resp) => {
                // display response
                setCurrentResponsePayload(JSON.stringify(resp.payload, null, "\t"));
                setLoading(false);
                toast.success(`Command Received`);
            })
            .catch((e) => {
                // show error
                setLoading(false);
                toast.error(parseAxiosError(e));
            });

    }

    const history = useHistory();

    const onReset = () => {
        reset();
        if (defaultThingID !== "") {
            defaultThingID = "";
            history.push('/commands');
        }
    }

    const setCommandFromMru = (c: IMruCommand) => {
        setMruOpen(false);

        if (c.thingIDs.length === 0) return;

        setThingID(c.thingIDs[0]);
        setCommand(c.command);
        setCommandValue(c.commandValue);
    }

    const setDeviceFromSearch = (thingID: string) => {
        setSearchOpen(false);
        setThingID(thingID);
    }

    const commandHeader: ReactNode = (
        <div className="devicesList_search">
            <ButtonGroup size='md'>
                <Button onClick={() => setSearchOpen(true)}>
                    <FontAwesomeIcon icon="search"/>
                </Button>
                <Button onClick={() => setMruOpen(!mruOpen)} ref={mruButtonRef}>
                    <FontAwesomeIcon icon="history"/>
                </Button>
                <Overlay
                    target={mruButtonRef}
                    show={mruOpen}
                    onHide={() => setMruOpen(false)}
                    rootClose
                    placement="bottom"
                >
                    <Popover id="deviceMru_popover" className="searchPopover">
                        <Popover.Content>
                            {MruService.mruCommands.length == 0 && "No MRU commands found"}
                            {MruService.mruCommands.map(c => (
                                <div key={c.uuid} className="mruContainer">
                                    <div>
                                        {c.command} - {c.commandValue}
                                    </div>
                                    {c.lastRequested > 0 ? new Date(c.lastRequested*1000).toLocaleString() : ""}
                                    <button style={{float: 'right'}} onClick={() => setCommandFromMru(c)}>
                                        <FontAwesomeIcon icon="search" />
                                    </button>
                                    {c.thingIDs.slice(0, Math.min(3, c.thingIDs.length)).map((t, i) => (
                                        <div key={i}>
                                            - {t}
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </Popover.Content>
                    </Popover>
                </Overlay>

                <Button onClick={onReset} disabled={loading || (thingID === "" && commandValue === "" && currentTopic === "")}>
                    <FontAwesomeIcon icon="window-close"/>
                </Button>
                <Button
                    disabled={!isValid}
                    loading={loading}
                    onClick={onSubmit}
                >
                    Publish
                </Button>
            </ButtonGroup>
        </div>
    );

    return (<>
        <Page>
            <PageHeader title={"Commands"} right={commandHeader}/>
            <Row>
                <Col>
                    <TextField
                        fullWidth
                        label="Thing ID"
                        spellCheck={false}
                        placeholder='Device.<Manufacturer>.<ModelNumber>.<SerialNumber>.<Location>'
                        disabled={defaultThingID !== ""}
                        value={thingID}
                        onChange={(e) => setThingID(e.target.value)}
                        required
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <Dropdown
                        fullWidth
                        label="Command"
                        options={CommandList}
                        value={command}
                        required
                        onChange={(e) => {if (e) setCommand(e)}}
                    />
                </Col>
                <Col>
                    <TextField
                        fullWidth
                        label="Command Value"
                        spellCheck={false}
                        placeholder='10'
                        value={commandValue}
                        onChange={(e) => setCommandValue(e.target.value)}
                        required
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <TextField
                        fullWidth
                        label="Publish Topic"
                        spellCheck={false}
                        value={currentTopic}
                        disabled
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <TextField
                        multiline
                        rows={12}
                        fullWidth
                        label="Publish Payload"
                        spellCheck={false}
                        value={currentCommandPayload}
                        disabled
                    />
                </Col>
                <Col>
                    <TextField
                        multiline
                        rows={12}
                        fullWidth
                        label="Response Payload"
                        spellCheck={false}
                        value={currentResponsePayload}
                        disabled
                    />
                </Col>
            </Row>
        </Page>

        <Toaster />
        
        <Modal show={searchOpen} onClose={() => setSearchOpen(false)} size='xl'>
            <DeviceSearchModal 
                key={1000}
                onSubmit={setDeviceFromSearch}
                onHide={() => setSearchOpen(false)}
            />
        </Modal>
    </>);
}
